import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChainStatus } from '../interfaces/chainStatus';
import { catchError, Observable, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ChainStatusService {
  private apiUrl = environment.apiUrl + 'chain-status';

  constructor(private http: HttpClient) {}

  setChainStatus(matchRunId: string, chainNumber: number, status: string, comment?: string, unselectReason?: string): Observable<ChainStatus> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = { matchRunId, chainNumber, status, comment, unselectReason };
    return this.http.post<ChainStatus>(this.apiUrl, body, { headers }).pipe(
      catchError((error) => {
        console.error('Error setting chain status:', error);
        return throwError(() => new Error('Failed to set chain status'));
      })
    );
  }

  selectForTransplantation(matchRunId: string, chainNumber: number, comment?: string): Observable<ChainStatus> {
    return this.setChainStatus(matchRunId, chainNumber, 'selected_for_transplantation', comment);
  }

  unselectForTransplantation(matchRunId: string, chainNumber: number, unselectReason: string): Observable<ChainStatus | { deleted: boolean }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = { matchRunId, chainNumber, status: 'unselected_for_transplantation', unselectReason };
    return this.http.post<ChainStatus | { deleted: boolean }>(this.apiUrl, body, { headers }).pipe(
      catchError((error) => {
        console.error('Error setting chain status:', error);
        return throwError(() => new Error('Failed to set chain status'));
      })
    );
  }

  confirmTransplantation(matchRunId: string, chainNumber: number, comment?: string): Observable<ChainStatus> {
    return this.setChainStatus(matchRunId, chainNumber, 'transplanted', comment);
  }

  getChainStatusByMatchRun(matchRunId: string): Observable<ChainStatus[]> {
    return this.http.get<ChainStatus[]>(`${this.apiUrl}/matchrun/${matchRunId}`).pipe(
      catchError((error) => {
        console.error('Error fetching chain statuses:', error);
        return throwError(() => new Error('Failed to fetch chain statuses'));
      })
    );
  }

  getChainStatus(matchRunId: string, chainNumber: number): Observable<ChainStatus> {
    return this.http.get<ChainStatus>(`${this.apiUrl}/${matchRunId}/${chainNumber}`).pipe(
      catchError((error) => {
        console.error('Error fetching chain status:', error);
        return throwError(() => new Error('Failed to fetch chain status'));
      })
    );
  }

  deleteChainStatus(matchRunId: string, chainNumber: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${matchRunId}/${chainNumber}`).pipe(
      catchError((error) => {
        console.error('Error deleting chain status:', error);
        return throwError(() => new Error('Failed to delete chain status'));
      })
    );
  }

  lockChainPartially(matchRunId: string, chainNumber: number, comment?: string): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = { comment };
    return this.http.post(`${this.apiUrl}/${matchRunId}/${chainNumber}/lock-partial`, body, { headers }).pipe(
      catchError((error) => {
        console.error('Error locking chain partially:', error);
        return throwError(() => new Error('Failed to lock chain partially'));
      })
    );
  }

  lockMergedChainFully(matchRunId: string, chainNumber: number, comment?: string): Observable<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = { comment };
    return this.http.post(`${this.apiUrl}/${matchRunId}/${chainNumber}/lock-merged-fully`, body, { headers }).pipe(
      catchError((error) => {
        console.error('Error locking merged chain:', error);
        return throwError(() => new Error('Failed to lock merged chain'));
      })
    );
  }

  unlockEntireChain(matchRunId: string, chainNumber: number): Observable<any> {
    return this.http.post(`${this.apiUrl}/${matchRunId}/${chainNumber}/unlock-entire`, {}).pipe(
      catchError((error) => {
        console.error('Error unlocking chain:', error);
        return throwError(() => new Error('Failed to unlock chain'));
      })
    );
  }

  setChainComment(matchRunId: string, chainNumber: number, comment: string): Observable<ChainStatus> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const body = { comment };
    return this.http.post<ChainStatus>(`${this.apiUrl}/${matchRunId}/${chainNumber}/comment`, body, { headers }).pipe(
      catchError((error) => {
        console.error('Error setting chain comment:', error);
        return throwError(() => new Error('Failed to set chain comment'));
      })
    );
  }
}
